<template>
  <main class="c-white bg-grey-10">
    <!-- Hero -->
    <section class="relative">
      <base-picture v-bind="hero.image" />
      <div
        v-style:p="{ sm: '0 10vw', md: '0' }"
        class="flex absolute-0 <md:mb-16 <md:flex-col <md:justify-center"
      >
        <div
          v-style="{ p: { md: '0 0 0 10vw' }, h: { md: '76.3%' } }"
          class="f-col items-start justify-center <md:mb-16 md:w-1/2"
        >
          <h1
            v-style:text-size="{ sm: '12vw', md: '4vw' }"
            class="fw-medium lh-none mb-6 md:ws-pre-wrap"
          >
            {{ hero.title }}
          </h1>
          <p v-style:text-size="{ sm: '4vw', md: '1.25vw' }" class="mb-6">
            {{ hero.paragraph }}
          </p>
          <vf-button
            class="mb-4"
            :size="{ sm: 'md', md: 'sm' }"
            variant="primary-inverse"
            @click="scrollToElement('activities')"
          >
            {{ hero.cta1 }}
          </vf-button>
          <vf-button
            :size="{ sm: 'md', md: 'sm' }"
            :to="hero.cta2.to"
            variant="tertiary-inverse"
          >
            {{ hero.cta2.title }}
          </vf-button>
        </div>
        <div class="flex items-center md:w-1/2">
          <div class="w-fit md:m-a">
            <div v-for="({ title, iconSrc }, key) in features" :key class="mb-6 flex center justify-start">
              <base-picture
                v-style:w="{ sm: '17vw', md: '8vw' }"
                v-bind="iconSrc"
                :alt="title"
                class="b b-white"
                style="padding: 1.5vw"
              />
              <h2 v-style:text-size="{ sm: '4vw', md: '1.25vw' }" class="fw-medium pl-6 uppercase">
                {{ title }}
              </h2>
            </div>
          </div>
        </div>
      </div>
      <custom-arrow-scroll
        class="m-5 bottom-0 right-0 absolute md:m-10"
        @click="scrollToElement('goFurther')"
      />
    </section>
    <!-- Go further -->
    <section class="b-b b-white relative md:flex" data-scroll-el="goFurther">
      <base-picture
        v-bind="goFurther.image"
        :alt="goFurther.title"
        class="h-full absolute md:w-2/5"
      />
      <div class="px-8 md:flex md:w-2/5 md:center">
        <div
          v-style:text-size="{ sm: '8.5vw', md: '3vw' }"
          class="fw-medium ws-pre-wrap relative !lh-none <md:pt-8"
        >
          <div
            class="mb-8 bg-white w-1/5"
            style="height:0.5rem"
          />{{ goFurther.title }}
        </div>
      </div>
      <div
        v-style:p="{ sm: '2rem 5rem 2rem 2rem', md: '2rem 5rem' }"
        class="relative overflow-hidden md:w-3/5"
      >
        <div
          v-style:text-size="{ sm: '6vw', md: '4vw' }"
          class="fw-light lh-tight top-1/2 relative md:transform"
          :style="`--un-translate-y:-${y * 0.025}rem`"
        >
          {{ goFurther.paragraph }}
        </div>
      </div>
    </section>
    <!-- Features -->
    <section class="bg-grey-20 md:b-b md:b-white">
      <base-grid :cols="{ sm: 1, md: 3 }">
        <div
          v-for="({ title, paragraph, iconSrc, backgroundSrc }, key) in features"
          :key
          class="group b-white f-col justify-center relative"
          :class="{ 'md:b-r': features.length - 1 !== key }"
        >
          <div
            class="text-center absolute space-y-6 duration group-hover:op-100 md:op-0"
            style="padding-inline: 4rem"
          >
            <base-picture v-style:w="{ sm: '28vw', md: '7vw' }" v-bind="iconSrc" />
            <h2 v-style:text-size="{ sm: '9vw', md: '3vw' }" class="fw-medium">
              {{ title }}
            </h2>
            <p v-style:text-size="{ sm: '4vw', md: '1.25vw' }">
              {{ paragraph }}
            </p>
          </div>
          <base-picture v-bind="backgroundSrc" :alt="title" />
        </div>
      </base-grid>
    </section>
    <!-- Choose your activity -->
    <section class="pt-8 text-center md:py-16" data-scroll-el="activities">
      <h2 v-style:text-size="{ sm: '8vw', md: '3vw' }" class="fw-medium mb-8">
        {{ activities.title }}
      </h2>
      <base-tabs-root :key="activeTab" v-model="activeTab">
        <base-tabs-list>
          <base-scroll class="mb-8">
            <div class="m-a" style="padding-inline: 4.5rem">
              <base-tabs-trigger
                v-for="({ title }, key) in activities.cats"
                :key
                v-style:text-size="{ sm: '4vw', md: '1.5vw' }"
                class="uppercase"
                :class="{ 'fw-bold underlined': activeTab === key }"
                :value="key"
              >
                {{ title }}
                <span v-if="key !== activities.cats.length - 1" class="mx-6">|</span>
              </base-tabs-trigger>
            </div>
          </base-scroll>
        </base-tabs-list>
        <base-tabs-content
          v-for="({ paragraph, prods, materials, colors, dotsContent }, catKey) in activities.cats"
          :key="catKey"
          class="text-center"
          :value="catKey"
        >
          <p v-style:text-size="{ sm: '4vw', md: '1.25vw' }" class="mb-8">
            {{ paragraph }}
          </p>
          <!-- Products carousel -->
          <base-scroll style="margin-bottom: 4rem">
            <div
              v-for="({ title, images, cta, gridImage }, prodKey) in prods"
              :key="prodKey"
              class="b b-white w-2/3 md:w-1/4"
              :class="{ 'ml-a': !prodKey, 'mr-a': prods.length - 1 === prodKey }"
            >
              <div style="padding: 4vw">
                <base-button
                  class="w-full"
                  @click="scrollToProduct(catKey, prodKey, gridImage || 0)"
                >
                  <base-picture
                    v-bind="images[gridImage || 0]"
                    class="mb-6 w-full duration hover:scale-105"
                  />
                </base-button>
                <br>
                <vf-button :to="cta.to" variant="tertiary-inverse">
                  {{ $t.shopNow }}
                </vf-button>
              </div>
              <base-button
                v-style:text-size="{ sm: '4vw', md: '1.25vw' }"
                class="fw-bold px-3 py-6 b-t b-white w-full uppercase"
                @click="scrollToProduct(catKey, prodKey)"
              >
                {{ title }}
              </base-button>
            </div>
          </base-scroll>
          <!-- Products characteristics -->
          <base-scroll ref="scroll" data-scroll-el="productCharacteristics">
            <div
              v-for="({
                cta,
                description,
                dotsPosition,
                images,
                materialPercentages,
                title,
                weight,
              }, prodKey) in prods"
              :key="prodKey"
              class="px-8 text-left w-full md:flex md:flex-row-reverse"
            >
              <!-- Right column -->
              <div class="relative md:flex md:w-3/5">
                <div
                  v-style:m=" { sm: '0 0 4rem 0', md: '0 0 0 55%' }"
                  class="md:w-1/3 md:bottom-1/2 md:absolute"
                >
                  <h2
                    v-style:text-size="{ sm: '5vw', md: '1.25vw' }"
                    class="fw-medium lh-none pb-3 b-b b-grey-30 uppercase"
                  >
                    {{ getActiveDot(catKey, prodKey).title }}
                  </h2>
                  <p v-style:text-size="{ sm: '4vw', md: '1.25vw' }" class="lh-tight pt-3">
                    {{ getActiveDot(catKey, prodKey).paragraph }}
                  </p>
                </div>
                <div
                  v-style:mb="{ sm: '4rem' }"
                  class="h-fit relative md:w-2/3"
                >
                  <base-button
                    v-for="(position, dotsPosKey) in dotsPosition"
                    :key="dotsPosKey"
                    class="p-1 b-2 rounded-full absolute duration"
                    :class="getActiveDot(catKey, prodKey) === dotsContent[dotsPosKey]
                      ? 'bg-black c-white b-white rotate-45' : 'bg-white c-black b-transparent'"
                    :style="position"
                    @click="prods[prodKey].activeDot = dotsContent[dotsPosKey]"
                  >
                    <vf-icon name="plus" />
                  </base-button>
                  <base-picture :alt="title" v-bind="getActiveImg(catKey, prodKey)" class="w-full" />
                </div>
              </div>
              <!-- Left column -->
              <div class="md:w-2/5">
                <h2
                  v-style:text-size="{ sm: '8vw', md: '2.75vw' }"
                  class="fw-medium lh-tight mb-6 ws-pre-wrap uppercase"
                >
                  {{ title }}
                </h2>
                <p
                  v-style="{ 'text-size': { sm: '4vw', md: '1.25vw' }, 'max-w': { md: '70%' } }"
                  class="mb-10"
                >
                  {{ description }}
                </p>
                <vf-button
                  class="mb-10"
                  :size="{ sm: 'md', md: 'sm' }"
                  :to="cta.to"
                  variant="tertiary-inverse"
                >
                  {{ $t.shopNow }}
                </vf-button>
                <p v-style:text-size="{ sm: '4vw', md: '1.25vw' }" class="mb-3 uppercase">
                  {{ activities.colors }}:
                </p>
                <div v-style:mb="{ sm: '4rem', md: '6rem' }" class="flex">
                  <div
                    v-for="({ color }, colKey) in colors"
                    :key="colKey"
                    class="mr-3 b b-transparent"
                    :class="{ 'b-white': images[colKey] === getActiveImg(catKey, prodKey) }"
                  >
                    <vf-color-picker
                      :color
                      size="sm"
                      variant="rectangle"
                      @click="prods[prodKey].activeImg = images[colKey]"
                    />
                  </div>
                </div>
                <!-- Materials composition -->
                <div class="flex <md:p-8 <md:bg-black <md:f-col <md:-mx-8">
                  <div
                    class="grid items-center md:pr-6 md:b-r md:b-grey-30"
                    style="grid-template-columns: auto 1fr; height: fit-content"
                  >
                    <p
                      v-style:text-size="{ sm: '3.5vw', md: '1.5vw' }"
                      class="fw-medium mb-3 col-span-2 uppercase"
                    >
                      {{ activities.materialComposition }}
                    </p>
                    <template v-for="(material, matKey) in materials" :key="matKey">
                      <p v-style:text-size="{ sm: '5.5vw', md: '1.5vw' }" class="pr-3">
                        {{ materialPercentages[matKey] }}
                      </p>
                      <p v-style:text-size="{ sm: '3.5vw', md: '1.25vw' }">
                        {{ material }}
                      </p>
                    </template>
                  </div>
                  <div
                    class="grid h-fit items-center <md:mt-6 md:ml-6"
                    style="grid-template-columns: auto 1fr"
                  >
                    <p
                      v-style:text-size="{ sm: '3.5vw', md: '1.5vw' }"
                      class="fw-medium mb-3 uppercase"
                      style="grid-column: span 2"
                    >
                      {{ activities.weight }}
                    </p>
                    <p v-style:text-size="{ sm: '5.5vw', md: '1.5vw' }" class="pr-3">
                      {{ weight[0] }}
                    </p>
                    <p v-style:text-size="{ sm: '3.5vw', md: '1.25vw' }">
                      {{ weight[1] }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </base-scroll>
        </base-tabs-content>
      </base-tabs-root>
    </section>
  </main>
</template>

<script lang="ts" setup>
import type { PageConfig } from '#brand/types/pages/our-socks'

definePageMeta({ header: { transparent: true } })

const activeTab = ref(0)
const scroll = ref<any[]>([])
const { y } = useScroll(window)
const route = useRoute()
const router = useRouter()
const { $t } = useNuxtApp()

const { data, error } = await useApi().internal.getStaticPage<PageConfig>('our-socks')
if (!data.value) throw createError({ ...error.value!, fatal: true })

const { hero, features, goFurther, activities } = data.value

const getActiveImg = (catKey, prodKey) =>
  activities.cats[catKey].prods[prodKey].activeImg || activities.cats[catKey].prods[prodKey].images[0]

const getActiveDot = (catKey, prodKey) =>
  activities.cats[catKey].prods[prodKey].activeDot || activities.cats[catKey].dotsContent[0]

const scrollToProduct = (catKey, prodKey, colorKey = 0) => {
  const prods = activities.cats[catKey].prods[prodKey]
  scrollToElement('productCharacteristics')
  scroll.value[0].scrollToIndex(prodKey)
  prods.activeImg = prods.images[colorKey]
}

onMounted(() => {
  if (route.query.activity) {
    const index = activities.cats.findIndex((each) => each.query === route.query.activity)
    index > 0 && scrollToElement('activities')
    activeTab.value = index
  }
  watch(activeTab, () => {
    router.push({ query: { activity: activities.cats[activeTab.value].query } })
  })
})
</script>
